import { useState, useEffect, useCallback, useMemo } from 'react'
import { RefreshCw, Trash2, Eye, Save, RotateCcw, CheckCircle2, CircleX, Inbox, Hash, Settings2 } from 'lucide-react'
import { IconLabel } from '@/components/ui/app-icon'
import { Button } from '@/components/ui/button'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { useToast } from '@/hooks/useToast'
import { advancedApi } from '@/lib/api'
import type { Registry, RegistryEntry, RegistryColumn } from '@/lib/api'
import { formatDateTime } from '@/lib/utils'

/* ═══════════════════════════════════════════
   Tab grouping helpers
   ═══════════════════════════════════════════ */

interface TabGroup {
  id: string
  title: string
  order: number
  categories: { id: string; cfg: RegistryEntry }[]
}

function buildTabGroups(registry: Registry): TabGroup[] {
  const groups: Record<string, TabGroup> = {}
  for (const [catId, cfg] of Object.entries(registry)) {
    const tabId = cfg.tab
    if (!groups[tabId]) {
      groups[tabId] = { id: tabId, title: cfg.tab_title, order: cfg.tab_order, categories: [] }
    }
    groups[tabId].categories.push({ id: catId, cfg })
  }
  return Object.values(groups).sort((a, b) => a.order - b.order)
}

/* ═══════════════════════════════════════════
   Badge color mapping
   ═══════════════════════════════════════════ */

const BADGE_COLORS: Record<string, string> = {
  green: 'bg-green-100 text-green-800',
  blue: 'bg-blue-100 text-blue-800',
  yellow: 'bg-yellow-100 text-yellow-800',
  red: 'bg-red-100 text-red-800',
  gray: 'bg-gray-100 text-gray-600',
}

/* ═══════════════════════════════════════════
   Cell renderer
   ═══════════════════════════════════════════ */

function CellValue({ col, value }: { col: RegistryColumn; value: unknown }) {
  if (value == null) return <span className="text-gray-300">—</span>

  if (col.type === 'bool') {
    return value ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <span className="text-gray-300">—</span>
  }

  if (col.type === 'date' && typeof value === 'string') {
    return <span>{formatDateTime(value)}</span>
  }

  if (col.badge && typeof value === 'string') {
    const color = col.badge[value] || 'gray'
    return (
      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${BADGE_COLORS[color] || BADGE_COLORS.gray}`}>
        {String(value)}
      </span>
    )
  }

  if (col.code) {
    return <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">{String(value)}</code>
  }

  return <span className="truncate block max-w-[250px]">{String(value)}</span>
}

/* ═══════════════════════════════════════════
   Detail modal
   ═══════════════════════════════════════════ */

function DetailModal({
  item,
  title,
  deletable,
  onClose,
  onDelete,
}: {
  item: Record<string, unknown>
  title: string
  deletable: boolean
  onClose: () => void
  onDelete: () => void
}) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-[90%] max-w-[700px] max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button className="text-gray-400 hover:text-red-500 text-xl" onClick={onClose}>&times;</button>
        </div>
        <div className="px-6 py-4 overflow-y-auto flex-1">
          {Object.entries(item)
            .filter(([k]) => !k.startsWith('_'))
            .map(([k, v]) => (
              <div key={k} className="flex gap-4 py-2 border-b border-gray-100 last:border-0">
                <div className="min-w-[160px] font-semibold text-gray-700 text-sm shrink-0">{k}</div>
                <div className="text-gray-600 text-sm break-all">
                  {typeof v === 'object' && v !== null ? (
                    <code className="bg-gray-100 px-2 py-0.5 rounded text-xs whitespace-pre-wrap">
                      {JSON.stringify(v, null, 2)}
                    </code>
                  ) : (
                    String(v ?? '')
                  )}
                </div>
              </div>
            ))}
        </div>
        <div className="flex justify-end gap-2 px-6 py-3 border-t">
          <Button variant="secondary" size="sm" onClick={onClose}>Закрыть</Button>
          {deletable && (
            <Button variant="danger" size="sm" onClick={onDelete}>
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Удалить
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════
   Table renderer
   ═══════════════════════════════════════════ */

function CategoryTable({
  categoryId,
  cfg,
}: {
  categoryId: string
  cfg: RegistryEntry
}) {
  const { toast } = useToast()
  const [items, setItems] = useState<Record<string, unknown>[]>([])
  const [loading, setLoading] = useState(true)
  const [activeFilter, setActiveFilter] = useState('')
  const [modalItem, setModalItem] = useState<Record<string, unknown> | null>(null)

  const load = useCallback(async (filter?: string) => {
    setLoading(true)
    try {
      const params = filter ? { filter } : undefined
      const { data } = await advancedApi.getCategory(categoryId, params)
      setItems((data as { items: Record<string, unknown>[] }).items || [])
    } catch {
      toast.error('Ошибка загрузки')
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId])

  useEffect(() => { load(activeFilter) }, [categoryId]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (itemId: string) => {
    if (!confirm(`Удалить ${cfg.title} «${itemId}»?`)) return
    try {
      await advancedApi.deleteItem(categoryId, itemId)
      toast.success(`Удалено: ${itemId}`)
      setModalItem(null)
      load(activeFilter)
    } catch {
      toast.error('Ошибка удаления')
    }
  }

  const handleFilterClick = (filterVal: string) => {
    setActiveFilter(filterVal)
    load(filterVal)
  }

  if (loading) return <LoadingSpinner className="py-12" />

  return (
    <div>
      {/* Inline filters */}
      {cfg.filters && (
        <div className="flex gap-2 flex-wrap mb-3">
          {cfg.filters.map(f => (
            <button
              key={f.value}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border-2 transition-all ${
                activeFilter === f.value
                  ? 'bg-blue-500 text-white border-blue-500'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-blue-400 hover:text-blue-500'
              }`}
              onClick={() => handleFilterClick(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      <p className="text-sm text-gray-500 mb-2">{cfg.title}: <strong>{items.length}</strong> записей</p>

      {items.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <div className="mb-2 flex justify-center"><Inbox className="h-8 w-8" /></div>
          <p>Нет данных</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gradient-to-r from-indigo-500 to-purple-600">
                {cfg.columns.map(col => (
                  <th key={col.key} className="px-3 py-2.5 text-left text-white text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                    {col.label}
                  </th>
                ))}
                <th className="px-3 py-2.5 text-white text-xs font-semibold uppercase">Действия</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item, idx) => {
                const itemId = String(item[cfg.id_field] ?? idx)
                return (
                  <tr
                    key={itemId}
                    className="hover:bg-indigo-50/50 cursor-pointer"
                    onDoubleClick={() => setModalItem(item)}
                  >
                    {cfg.columns.map(col => (
                      <td key={col.key} className="px-3 py-2">
                        <CellValue col={col} value={item[col.key]} />
                      </td>
                    ))}
                    <td className="px-3 py-2 whitespace-nowrap">
                      <button
                        className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 mr-1"
                        onClick={e => { e.stopPropagation(); setModalItem(item) }}
                      >
                        <Eye className="w-3 h-3 mr-0.5" /> Просмотр
                      </button>
                      {cfg.deletable && (
                        <button
                          className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100"
                          onClick={e => { e.stopPropagation(); handleDelete(itemId) }}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail modal */}
      {modalItem && (
        <DetailModal
          item={modalItem}
          title={`${cfg.title} — ${modalItem[cfg.id_field]}`}
          deletable={cfg.deletable}
          onClose={() => setModalItem(null)}
          onDelete={() => handleDelete(String(modalItem[cfg.id_field]))}
        />
      )}
    </div>
  )
}

/* ═══════════════════════════════════════════
   Constants renderer (SSCC counters + settings JSON)
   ═══════════════════════════════════════════ */

interface SsccCounters {
  box_serial: number
  pallet_serial: number
  ai21_serial: number
  [key: string]: number
}

function ConstantsEditor() {
  const { toast } = useToast()
  const [loading, setLoading] = useState(true)
  const [boxSerial, setBoxSerial] = useState(0)
  const [palletSerial, setPalletSerial] = useState(0)
  const [ai21Serial, setAi21Serial] = useState(0)
  const [extraCounters, setExtraCounters] = useState<Record<string, number>>({})
  const [settingsJson, setSettingsJson] = useState('')
  const [jsonError, setJsonError] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await advancedApi.getCategory('constants')
      const d = data as { sscc_counters: Partial<SsccCounters>; settings: Record<string, unknown> }
      const counters = d.sscc_counters ?? {}
      setBoxSerial(Number(counters.box_serial ?? 0))
      setPalletSerial(Number(counters.pallet_serial ?? 0))
      setAi21Serial(Number(counters.ai21_serial ?? 0))
      const known = new Set(['box_serial', 'pallet_serial', 'ai21_serial'])
      const extras: Record<string, number> = {}
      for (const [k, v] of Object.entries(counters)) {
        if (!known.has(k)) extras[k] = Number(v)
      }
      setExtraCounters(extras)
      setSettingsJson(JSON.stringify(d.settings, null, 2))
    } catch {
      toast.error('Ошибка загрузки констант')
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => { load() }, [load])

  const saveSSCC = async () => {
    try {
      const payload: Record<string, number> = {
        box_serial: boxSerial,
        pallet_serial: palletSerial,
        ai21_serial: ai21Serial,
        ...extraCounters,
      }
      await advancedApi.saveConstants('sscc_counters', payload)
      toast.success('Счётчики SSCC сохранены')
    } catch {
      toast.error('Ошибка сохранения')
    }
  }

  const resetSSCC = async () => {
    if (!confirm('Сбросить счётчики SSCC?')) return
    try {
      await advancedApi.saveConstants('sscc_reset', {})
      toast.success('Счётчики сброшены')
      load()
    } catch {
      toast.error('Ошибка сброса')
    }
  }

  const saveSettings = async () => {
    let parsed: unknown
    try {
      parsed = JSON.parse(settingsJson)
      setJsonError(false)
    } catch {
      setJsonError(true)
      toast.error('Невалидный JSON')
      return
    }
    try {
      await advancedApi.saveConstants('settings', parsed)
      toast.success('Настройки сохранены')
    } catch {
      toast.error('Ошибка сохранения')
    }
  }

  if (loading) return <LoadingSpinner className="py-12" />

  return (
    <div className="space-y-6">
      {/* SSCC counters */}
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="text-lg font-semibold mb-4 inline-flex items-center gap-2"><Hash className="h-5 w-5" /> Счётчики SSCC / AI 21</h3>
        <div className="space-y-3">
          <div className="flex items-center gap-4">
            <label className="min-w-[140px] font-semibold text-sm text-gray-700">box_serial</label>
            <input
              type="number"
              className="border-2 border-gray-300 rounded-md px-3 py-1.5 text-sm w-48 focus:border-blue-500 focus:outline-none"
              value={boxSerial}
              onChange={e => setBoxSerial(Number(e.target.value))}
            />
            <span className="text-xs text-gray-500">серийник SSCC коробки</span>
          </div>
          <div className="flex items-center gap-4">
            <label className="min-w-[140px] font-semibold text-sm text-gray-700">pallet_serial</label>
            <input
              type="number"
              className="border-2 border-gray-300 rounded-md px-3 py-1.5 text-sm w-48 focus:border-blue-500 focus:outline-none"
              value={palletSerial}
              onChange={e => setPalletSerial(Number(e.target.value))}
            />
            <span className="text-xs text-gray-500">серийник SSCC паллеты</span>
          </div>
          <div className="flex items-center gap-4">
            <label className="min-w-[140px] font-semibold text-sm text-gray-700">ai21_serial</label>
            <input
              type="number"
              className="border-2 border-gray-300 rounded-md px-3 py-1.5 text-sm w-48 focus:border-blue-500 focus:outline-none"
              value={ai21Serial}
              onChange={e => setAi21Serial(Number(e.target.value))}
            />
            <span className="text-xs text-gray-500">серийник товара (AI 21, КМ)</span>
          </div>
          {Object.entries(extraCounters).map(([key, val]) => (
            <div key={key} className="flex items-center gap-4">
              <label className="min-w-[140px] font-semibold text-sm text-gray-700">{key}</label>
              <input
                type="number"
                className="border-2 border-gray-300 rounded-md px-3 py-1.5 text-sm w-48 focus:border-blue-500 focus:outline-none"
                value={val}
                onChange={e => setExtraCounters(prev => ({ ...prev, [key]: Number(e.target.value) }))}
              />
              <span className="text-xs text-gray-500">кастомный счётчик</span>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-4">
          <Button size="sm" onClick={saveSSCC}>
            <Save className="w-4 h-4 mr-1" /> Сохранить счётчики
          </Button>
          <Button variant="danger" size="sm" onClick={resetSSCC}>
            <RotateCcw className="w-4 h-4 mr-1" /> Сбросить
          </Button>
        </div>
      </div>

      {/* Settings JSON */}
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="text-lg font-semibold mb-4 inline-flex items-center gap-2"><Settings2 className="h-5 w-5" /> Настройки системы (JSON)</h3>
        <textarea
          className={`w-full min-h-[200px] font-mono text-sm p-4 border-2 rounded-lg resize-y focus:outline-none ${
            jsonError ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-gray-50 focus:border-blue-500 focus:bg-white'
          }`}
          value={settingsJson}
          onChange={e => { setSettingsJson(e.target.value); setJsonError(false) }}
        />
        <div className="mt-3">
          <Button size="sm" onClick={saveSettings}>
            <Save className="w-4 h-4 mr-1" /> Сохранить настройки
          </Button>
        </div>
      </div>
    </div>
  )
}

/* SidebarEditor removed — navigation managed exclusively via /admin/sidebar-settings */

/* ═══════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════ */

export function AdvancedPage() {
  const { toast } = useToast()
  const [registry, setRegistry] = useState<Registry | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<string | null>(null)
  const [activeCategory, setActiveCategory] = useState<string | null>(null)

  useEffect(() => {
    ;(async () => {
      try {
        const { data } = await advancedApi.getRegistry()
        setRegistry(data)
      } catch {
        toast.error('Ошибка загрузки реестра')
      } finally {
        setIsLoading(false)
      }
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const tabGroups = useMemo(() => (registry ? buildTabGroups(registry) : []), [registry])

  // Activate first tab + first category on load
  useEffect(() => {
    if (tabGroups.length && !activeTab) {
      const firstTab = tabGroups[0]
      setActiveTab(firstTab.id)
      if (firstTab.categories.length) {
        setActiveCategory(firstTab.categories[0].id)
      }
    }
  }, [tabGroups, activeTab])

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId)
    const tab = tabGroups.find(t => t.id === tabId)
    if (tab?.categories.length) {
      setActiveCategory(tab.categories[0].id)
    }
  }

  const currentTab = tabGroups.find(t => t.id === activeTab)
  const currentCfg = activeCategory && registry ? registry[activeCategory] : null

  if (isLoading) return <LoadingSpinner className="mt-20" />

  if (!registry || !tabGroups.length) {
    return (
      <div className="text-center py-20 text-gray-400">
        <div className="mb-2 flex justify-center"><CircleX className="h-8 w-8" /></div>
        <p>Реестр пуст</p>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-900"><IconLabel icon="advanced">Тонкие настройки</IconLabel></h1>
        <Button variant="ghost" size="sm" onClick={() => window.location.reload()}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Main tabs */}
      <div className="flex gap-0 border-b-2 border-gray-200 overflow-x-auto mb-4">
        {tabGroups.map(tab => (
          <button
            key={tab.id}
            className={`px-5 py-3 text-sm font-semibold transition-all border-b-3 whitespace-nowrap ${
              activeTab === tab.id
                ? 'text-blue-600 border-blue-500'
                : 'text-gray-500 border-transparent hover:text-blue-400 hover:bg-blue-50/50'
            }`}
            onClick={() => handleTabClick(tab.id)}
          >
            {tab.title}
          </button>
        ))}
      </div>

      {/* Sub-tabs (categories within the current tab) */}
      {currentTab && currentTab.categories.length > 1 && (
        <div className="flex gap-2 flex-wrap mb-4">
          {currentTab.categories.map(cat => (
            <button
              key={cat.id}
              className={`px-4 py-1.5 rounded-full text-sm font-medium border-2 transition-all ${
                activeCategory === cat.id
                  ? 'bg-blue-500 text-white border-blue-500'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-blue-400 hover:text-blue-500'
              }`}
              onClick={() => setActiveCategory(cat.id)}
            >
              {cat.cfg.title}
            </button>
          ))}
        </div>
      )}

      {/* Content area */}
      {activeCategory && currentCfg && (
        <div key={activeCategory}>
          {currentCfg.render_type === 'constants' ? (
            <ConstantsEditor />
          ) : (
            <CategoryTable categoryId={activeCategory} cfg={currentCfg} />
          )}
        </div>
      )}
    </div>
  )
}
